import React, { Dispatch, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TextareaAutosize } from "@mui/material";
import { Button, IconButton, Switch, Tooltip } from "@bolt/components";
import {
  ChatMessage,
  ChatMessageUser,
  ChatTheme,
  Conversation,
  ERRORS,
  ErrorType,
  HttpErrorType,
  ModelSupportedFileTypes,
  AppType,
} from "../../../types";
import "katex/dist/katex.min.css";
import ChatMessageComponent from "../../ChatMessageComponent";
import { MessageBot } from "../../MessageBot";
import SkeletonTextBlock from "../../SkeletonTextBlock";
import { LogOutButton } from "../../LogOutButton";
import MobileSidebarToggleButton from "../../MobileSidebarToggleButton";
import ChatFooterDisclosure from "../../ChatFooterDisclosure";
import clsx from "clsx";
import { useUserSessionContext } from "../../../contexts/UserSessionContext";
import { ChatHistory } from "../../ChatHistory";
import LoadingSection from "../../LoadingSection";
import BotWelcomeMessage from "../../BotWelcomeMessage";
import { FileUpload } from "../../FileUpload";
import { FilePreview } from "../../FilePreview";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import { ChatToolbar } from "../../ChatToolbar";
import { CharacterLimitWarning } from "../../CharacterLimitWarning";
import { MAX_CHAR_LIMIT, MAX_FILE_SIZE } from "../../../constants";
import { chatMessageErrorHandler, formatChatRequest, notifyError } from "../../../utils";
import { useTheme } from "../../../contexts/ThemeContext";
import { formatFileType } from "../../../utils/formatFileType";
import {
  useDeleteChatConversation,
  useFileUploadConfig,
  useGetChatConversationMessages,
  useGetChatConversations,
  useHttpErrorHandler,
  useLLMModel,
  usePostNewChat,
  usePostQuestion,
  usePostRephraseQuestion,
  useUserQuestion,
} from "../../../hooks";
import { GenAIButton } from "../../GenAIButton";
import { BulbIcon, WebIcon } from "../../Icons";
import { FileIcon } from "../../FileIcon";
import { buildErrorMessage } from "../../../utils";
import { ModelDropdown } from "../../ModelDropdown";
import { DisclosureWarning } from "../../DisclosureWarning";
import { AppSelector } from "../../AppSelector";
import { ChatGPCLogomark } from "../../Logos/Logos";

import "./ChatUiStyles.css";

const MIN_USER_REPHRASE_LENGTH = 10;

export const ChatGpcUi = ({
  activeApp,
  toggleSidebar,
  setActiveApp,
}: {
  activeApp: AppType;
  toggleSidebar: () => void;
  setActiveApp: Dispatch<AppType>;
}) => {
  const { isWebSearchFeatureEnabled, isReasoningEnabled } = useFeatureFlags();
  const { setSelectedModelId, selectedModelId, providers, modelInfo, resetModel } = useLLMModel();
  const { setUserSession } = useUserSessionContext();
  const { theme, toggleTheme } = useTheme();
  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);
  const [isWebSearchOn, setIsWebSearchOn] = useState(false);
  const [isReasoningOn, setIsReasoningOn] = useState(false);

  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const [chatState, setChatState] = useState<"ready" | "responding" | "rephrasing">("ready");
  const [chatID, setChatID] = useState<string | null>(null);
  const chatsContainerRef = useRef<HTMLDivElement | null>(null);
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null);

  const { userQuestion, setUserQuestion, clearQuestionContext, rephrase, file, setFile } = useUserQuestion();
  const { fetch: rephraseQuestion } = usePostRephraseQuestion();
  const { fetch: deleteChat } = useDeleteChatConversation();
  const { fetch: askQuestion, abort: abortQuestion } = usePostQuestion();
  const { fetch: createNewChat } = usePostNewChat();
  const { fetch: getConversationMessages, loading: isLoadingConversationMessages } = useGetChatConversationMessages();
  const {
    fetch: getChatConversations,
    loading: isLoadingChatConversations,
    error: errorLoadingChatConversations,
    data: chatConversations,
  } = useGetChatConversations();

  const { isFileUploadSupported, fileUploadAcceptedFilesFormatted, uploadFileTooltipText, uploadFileButtonText } =
    useFileUploadConfig(modelInfo?.supportedFileTypes);

  const displayFileUploadPreview = useMemo(() => ["ready", "rephrasing"].includes(chatState), [chatState]);

  useEffect(() => {
    getChatConversations();
  }, [getChatConversations]);
  const { handleHttpError } = useHttpErrorHandler();

  useEffect(() => {
    if (chatsContainerRef.current) chatsContainerRef.current.scrollTop = chatsContainerRef.current.scrollHeight;
  }, [chatHistory]);

  useEffect(() => {
    if (chatState === "ready") focusTextInput();
  }, [chatState]);

  useEffect(() => {
    setIsCharacterLimitExceeded(userQuestion.length > MAX_CHAR_LIMIT);
  }, [userQuestion]);

  const focusTextInput = () => {
    if (!textAreaRef.current) return;
    const textArea = textAreaRef.current;
    textArea.focus();
    textArea.setSelectionRange(textArea.value.length, textArea.value.length);
  };

  const handleAbort = useCallback(() => {
    abortQuestion();
    clearQuestionContext();
    setChatHistory((state) => [...state, buildErrorMessage({ error: ERRORS.ABORTED, errorType: ErrorType.OTHER })]);
    setChatState("ready");
  }, [abortQuestion, clearQuestionContext]);

  const setupChat = useCallback(
    (conversation: Conversation | null) => {
      abortQuestion();
      clearQuestionContext();
      setFile(undefined);

      if (!conversation) {
        setChatID(null);
        resetModel();
        setChatHistory([]);
        return;
      }

      setChatID(conversation.id);
      setSelectedModelId(conversation.model);

      getConversationMessages({
        url: `${process.env.REACT_APP_BACKEND_URL}/api/v2/chat/conversations/${conversation.id}/messages`,
      })
        .then((res) => {
          if (res && res.data) {
            setChatHistory(res.data);
          }
        })
        .catch((error: HttpErrorType) => {
          handleHttpError(error);
          setChatHistory([]);
        });
    },
    [
      abortQuestion,
      clearQuestionContext,
      setFile,
      setSelectedModelId,
      getConversationMessages,
      handleHttpError,
      resetModel,
    ],
  );

  const handleNewChat = useCallback(() => setupChat(null), [setupChat]);

  const deleteConversation = useCallback(
    async (conversation: Conversation) => {
      if (window.confirm(`Do you want to delete item "${conversation.title}"?`)) {
        await deleteChat({
          url: `${process.env.REACT_APP_BACKEND_URL}/api/v2/chat/conversations/${conversation.id}`,
        });

        getChatConversations();
      }
    },
    [deleteChat, getChatConversations],
  );

  const handleSend = async () => {
    clearQuestionContext();
    await sendMessage(userQuestion, file);
    setFile(undefined);
  };

  const sendMessage = useCallback(
    async (message: string, fileUpload?: File) => {
      setChatState("responding");
      setIsWebSearchOn(false);
      setChatHistory((state) => [
        ...state,
        {
          content: message,
          role: "user",
          error: false,
          file_name: fileUpload?.name,
          file_type: fileUpload?.type as ModelSupportedFileTypes,
          file: fileUpload,
        },
      ]);

      try {
        let currentChatId = chatID;
        if (!currentChatId) {
          const response = await createNewChat({
            body: {
              model: selectedModelId,
            },
          });
          if (!response?.data) {
            setChatHistory((state) => [
              ...state,
              buildErrorMessage({ error: ERRORS.CHAT_INITIALIZATION_FAILED, errorType: ErrorType.OTHER }),
            ]);
            return;
          }
          currentChatId = response.data;
          setChatID(currentChatId);
        }
        const result = await askQuestion({
          body: formatChatRequest(currentChatId, message, isWebSearchOn, isReasoningOn, fileUpload),
        });
        if (result?.data) {
          setChatHistory((state) => [...state, result.data as ChatMessage]);
        }
      } catch (error: any) {
        setChatHistory((state) => [...state, buildErrorMessage(chatMessageErrorHandler(error))]);
      } finally {
        getChatConversations();
        setChatState("ready");
      }
    },
    [
      createNewChat,
      askQuestion,
      chatID,
      isWebSearchOn,
      isReasoningOn,
      getChatConversations,
      selectedModelId,
      setIsWebSearchOn,
    ],
  );

  const handleRephrase = () => {
    setChatState("rephrasing");
    rephraseQuestion({
      body: { prompt: userQuestion },
    })
      .then((response) => {
        if (response?.data) {
          rephrase.push(response.data.rephrasedPrompt);
        }
      })
      .catch((error: HttpErrorType) => {
        handleHttpError(error);
      })
      .finally(() => {
        setChatState("ready");
      });
  };

  const handleRegenerateResponse = useCallback(() => {
    const userQuestion = chatHistory
      .slice()
      .reverse()
      .find((message: ChatMessage): message is ChatMessageUser => message.role === "user");

    if (userQuestion) {
      sendMessage(userQuestion.content, userQuestion.file);
    } else {
      console.error("The user question from chatHistory is empty!");
    }
  }, [sendMessage, chatHistory]);

  const onKeyDownTextarea = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      if (isCharacterLimitExceeded || userQuestion.length === 0) {
        e.preventDefault();
        return;
      }
      handleSend();
      e.preventDefault();
    }
  };

  const handleRedirectToRoot = useCallback(() => {
    setUserSession(null);
  }, [setUserSession]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!isFileUploadSupported(file.type)) {
      notifyError(
        `Invalid file format. Only following types are allowed: ${fileUploadAcceptedFilesFormatted.join(", ")}`,
      );
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      notifyError("File too big. Max file size is 8 Mb.");
      return;
    }

    setFile(file);
  };

  const onRemoveFileClickHandler = () => {
    setFile(undefined);
  };

  const toggleWebSearch = () => {
    setIsWebSearchOn((isOn) => !isOn);
  };

  const toggleReasoning = () => {
    setIsReasoningOn((isOn) => !isOn);
  };

  const handleOnModelChange = async (modelId: string) => {
    await handleNewChat();
    setSelectedModelId(modelId);
  };
  return (
    <>
      <div className="sidebar">
        <AppSelector selectedApp={activeApp} setApp={setActiveApp} />
        <Button
          className="new-chat-button"
          size="lg"
          intent="primary"
          data-testid="clear-history-btn"
          onClick={handleNewChat}
          disabled={!chatID}
        >
          New chat
        </Button>
        <ChatHistory
          activeConversationId={chatID}
          onItemClick={setupChat}
          onItemDelete={deleteConversation}
          isLoadingChatConversations={isLoadingChatConversations && !chatConversations}
          errorLoadingChatConversations={errorLoadingChatConversations}
          chatConversations={chatConversations}
        />
        <div className="theme-switch-container">
          <Switch
            data-testid="dark-mode-toggle"
            onClick={toggleTheme}
            label="dark mode"
            size="md"
            checked={theme === ChatTheme.dark}
          />
        </div>
      </div>
      <div className="main">
        <div className="chat-header">
          <MobileSidebarToggleButton onClick={toggleSidebar} />
          {providers.providers.length > 0 && selectedModelId && (
            <ModelDropdown selectedModel={selectedModelId} providers={providers} onChange={handleOnModelChange} />
          )}
          <DisclosureWarning />
          <LogOutButton />
        </div>
        {isLoadingConversationMessages ? (
          <LoadingSection />
        ) : (
          <div className="chats" data-testid="chats" ref={chatsContainerRef}>
            <BotWelcomeMessage icon={<ChatGPCLogomark />} />

            {chatHistory.map((message: ChatMessage, index: number) => (
              <ChatMessageComponent
                key={index}
                message={message}
                handleRegenerateResponse={handleRegenerateResponse}
                handleRedirectToRoot={handleRedirectToRoot}
                isLatest={index === chatHistory.length - 1}
                botIcon={<ChatGPCLogomark />}
              />
            ))}

            {chatState === "responding" && (
              <MessageBot icon={<ChatGPCLogomark />}>
                <div style={{ width: "100%" }}>
                  <SkeletonTextBlock />
                </div>
              </MessageBot>
            )}
          </div>
        )}
        <div
          className={`input-box-container ${isCharacterLimitExceeded ? "input-box-container-error" : ""}`}
          onClick={(e) => {
            if (e.target === e.currentTarget) focusTextInput();
          }}
        >
          <div
            className={clsx("chat-text-area", {
              "error-border": isCharacterLimitExceeded,
            })}
          >
            {displayFileUploadPreview && file && (
              <FilePreview
                onRemoveFileClick={onRemoveFileClickHandler}
                fileName={file.name}
                fileType={formatFileType(file.type)}
                icon={<FileIcon fileType={file.type} />}
              />
            )}
            <TextareaAutosize
              ref={textAreaRef}
              data-testid="chat-input"
              aria-label="Ask chatbot"
              draggable={false}
              disabled={chatState !== "ready"}
              placeholder="Ask me a question"
              onKeyDown={onKeyDownTextarea}
              onChange={(e: React.FormEvent<HTMLTextAreaElement>) => setUserQuestion(e.currentTarget.value)}
              value={userQuestion}
            />
          </div>
          <ChatToolbar
            left={
              <>
                {modelInfo && modelInfo?.supportedFileTypes?.length > 0 && (
                  <FileUpload
                    onChange={handleFileChange}
                    acceptedFiles={modelInfo.supportedFileTypes}
                    disabled={!!file || isWebSearchOn}
                    text={uploadFileButtonText}
                    tooltipText={uploadFileTooltipText}
                  />
                )}
                {isWebSearchFeatureEnabled && (
                  <GenAIButton
                    icon={<WebIcon />}
                    isActive={isWebSearchOn}
                    onClick={toggleWebSearch}
                    disabled={!!file}
                    dataTestId={"web-search-button"}
                  >
                    Search
                  </GenAIButton>
                )}
                {isReasoningEnabled && (
                  <GenAIButton
                    icon={<BulbIcon />}
                    isActive={isReasoningOn}
                    onClick={toggleReasoning}
                    dataTestId={"reasoning-button"}
                  >
                    Reason
                  </GenAIButton>
                )}
              </>
            }
            right={
              <>
                {isCharacterLimitExceeded && <CharacterLimitWarning text={userQuestion} />}
                {chatState === "rephrasing" && <div className="rephrase-status">Rephrasing...</div>}
                {chatState === "ready" && rephrase.canRevert && (
                  <Button
                    className="rephrase-undo"
                    iconLeft="clock-rotate-left"
                    onClick={rephrase.revert}
                    intent="primary"
                    variant="ghost"
                    size="md"
                  >
                    Undo rephrase
                  </Button>
                )}
                {chatState === "ready" && (
                  <Tooltip content="Rephrase my prompt (beta)" color="inverted">
                    <IconButton
                      ariaLabel="Rephrase my prompt (beta)"
                      onClick={handleRephrase}
                      className="rephrase-button"
                      data-testid="rephrase-button"
                      size="md"
                      iconName="sparkles"
                      variant="outline"
                      disabled={userQuestion.length <= MIN_USER_REPHRASE_LENGTH || isCharacterLimitExceeded}
                    />
                  </Tooltip>
                )}
                {chatState !== "ready" && (
                  <Tooltip content="Stop response" color="inverted">
                    <IconButton
                      ariaLabel="Stop response"
                      onClick={handleAbort}
                      className="abort-button"
                      data-testid="abort-button"
                      size="md"
                      iconName="xmark-large"
                      variant="outline"
                    />
                  </Tooltip>
                )}
                {chatState === "ready" && (
                  <Tooltip content="Submit my prompt" color="inverted">
                    <IconButton
                      ariaLabel="Submit my prompt"
                      disabled={userQuestion === "" || isCharacterLimitExceeded}
                      className="send-button"
                      data-testid="send-button"
                      onClick={handleSend}
                      size="md"
                      iconName="chevron-right"
                      variant="outline"
                    />
                  </Tooltip>
                )}
              </>
            }
          />
        </div>
        <ChatFooterDisclosure />
      </div>
    </>
  );
};
